import { DocumentMapping } from "@/configuration/document-mappings/types";
import { CVVC_PLACEHOLDER_ASSIGNEE } from "@/configuration/groups/cvvc/departments";
import { Classification } from "@/src/models/constants/classifications";

// CVVC's document taxonomy mirrors StrideCare's near-exactly (same Athena practice, same document
// type IDs) - see the index-mapping sheet under source/. Assignee values marked with
// CVVC_PLACEHOLDER_ASSIGNEE are pending CVVC's real per-location Athena bucket names.
export const CVVC_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = {
  [Classification.CLEARANCE_REQUEST_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Cardiac clearances",
    documentLabel: "Cardiac Clearance",
    documentTypeId: 309005,
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.CLEARANCE_REQUEST_RESPONSE,
    group: "cvvc",
  },
  [Classification.DME_ORDER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "DME Order to be signed",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.DME_ORDER,
    group: "cvvc",
  },
  [Classification.ADMIN_DISABILITY_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Disability/FMLA forms",
    documentLabel: "Request for Additional Information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ADMIN_DISABILITY_FORMS,
    group: "cvvc",
  },
  // Sheet cell was mangled (embedded newlines); read as "Upload normally; if no patient match in
  // Athena, email Tarynne Banner (Tarynne.Banner@stridecare.com) instead of the default wait-then-
  // upload behavior." The email-fallback branch is workflow-steps.ts logic (phase 6), not a
  // document-mappings field - confirmed with Jack, not yet implemented.
  [Classification.FINANCIAL_BILLING_RECORDS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Financial records",
    documentLabel: "Valerie Health - Financial Document",
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.FINANCIAL_BILLING_RECORDS,
    group: "cvvc",
  },
  [Classification.IMAGING_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Imaging results",
    documentLabel: "{{ imagingType }}",
    folder: "Imaging/Diagnostic Result",
    folderMapping: "Imaging/Diagnostic Result",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.IMAGING_RESULTS,
    group: "cvvc",
  },
  [Classification.LAB_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Lab results",
    documentLabel: "Lab*",
    documentTypeId: 400537,
    folder: "Lab Results",
    folderMapping: "Lab Results",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.LAB_RESULTS,
    group: "cvvc",
  },
  [Classification.LEGAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Close Document"],
    assignedTo: null,
    displayName: "Power of attorney documents",
    documentLabel: "Health Care Power of Attorney",
    documentTypeId: 386202,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.LEGAL,
    group: "cvvc",
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "chartmate",
    displayName: "Medical record requests",
    documentLabel: "Medical Record Request",
    documentTypeId: 271399,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: "cvvc",
  },
  [Classification.ORDER_LAB]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Lab orders",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ORDER_LAB,
    group: "cvvc",
  },
  // Sheet uses "Care Plan Oversight" here (not the "{{ imagingType }}" label StrideCare uses for
  // this classification) - CVVC's own value, not a transcription error; flagged in the PR.
  [Classification.ORDER_IMAGING]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Imaging orders",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ORDER_IMAGING,
    group: "cvvc",
  },
  [Classification.CARE_PLAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Plan of care",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.CARE_PLAN,
    group: "cvvc",
  },
  [Classification.PHARMACY_RELATED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Any Rx document",
    documentLabel: "",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PHARMACY_RELATED,
    group: "cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Prior auth approval/denials medications",
    documentLabel: "Insurance {{ authorizationStatus }} - Medication",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    group: "cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "cvneedsubmission",
    displayName: "Prior auth approval/denials for procedures",
    documentLabel: "Insurance {{ authorizationStatus }} - Procedure",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    group: "cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Prior auth approval/denials for imaging",
    documentLabel: "Insurance {{ authorizationStatus }} - Imaging",
    folder: "Lab/Imaging",
    folderMapping: "Lab/Imaging",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE,
    group: "cvvc",
  },
  // Covers both "medical supplies" and "services (home health)" prior-auth approvals/denials from
  // the sheet - neither has a dedicated Classification sub-type; collapsed into the generic
  // catch-all per Jack's call, rather than colliding with PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE
  // (which already owns the "cvneedsubmission" bucket for row 19).
  [Classification.PRIOR_AUTHORIZATION]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Prior auth approval/denials for medical supplies or services",
    documentLabel: "Insurance {{ authorizationStatus }} - Supplies/Services",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION,
    group: "cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "cvneedsappeal",
    displayName: "Prior auth request for more information",
    documentLabel: "Request for additional information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    group: "cvvc",
  },
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Operative or procedural reports",
    documentLabel: "Operative Report",
    documentTypeId: 342771,
    folder: "Clinic Document - Operative Report",
    folderMapping: "Clinic Document - Operative Report",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
    group: "cvvc",
  },
  [Classification.PROGRESS_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Close Document"],
    assignedTo: null,
    displayName: "Any progress note/medical history/etc",
    documentLabel: "Historical Medical Record",
    folder: "Medical Record Document - Historical Medical Record",
    folderMapping: "Medical Record Document - Historical Medical Record",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PROGRESS_NOTE,
    group: "cvvc",
  },
  [Classification.INSURANCE_CARD]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Close Document"],
    assignedTo: null,
    displayName: "Insurance fax back request (verification of benefits information)",
    documentLabel: "Explanation of Benefits",
    documentTypeId: 390183,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.INSURANCE_CARD,
    group: "cvvc",
  },
  // Email-only, no Athena upload (folder/assignee null); recipient (priscilla.hall@stridecare.com)
  // is routing.ts territory - phase 5.
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "HEDIS & Risk Management",
    documentLabel: "Valerie Health Document",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.MEDICAL_RECORD_REQUEST_HEDIS,
    group: "cvvc",
  },
  // No direct StrideCare analog for "Non patient information." Tentatively the generic ADMIN
  // catch-all - flagged for confirmation before this locks in. Email-only (recipient
  // admin_cvvc@stridecare.com is routing.ts territory - phase 5).
  [Classification.ADMIN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Non patient information",
    documentLabel: "Valerie Health Document",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.ADMIN,
    group: "cvvc",
  },
  [Classification.REFERRAL_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Requesting a referral from StrideCare CVVC",
    documentLabel: "Request for Additional Information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.REFERRAL_REQUEST,
    group: "cvvc",
  },
  [Classification.REFERRAL_STATUS_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: CVVC_PLACEHOLDER_ASSIGNEE,
    displayName: "Update on referral StrideCare CVVC sent",
    documentLabel: "Referral Declined/Not Scheduled",
    documentTypeId: 390844,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.REFERRAL_STATUS_REQUEST,
    group: "cvvc",
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Junk",
    documentLabel: "Junk",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    classification: Classification.JUNK,
    group: "cvvc",
  },
  // Rich case-creation workflow (Athena case create + eligibility check + referral email + auth
  // step), matching StrideCare's REFERRAL - not the sheet's literal "Upload + Close the document"
  // (confirmed with Jack; the sheet's separate Referral Mapping tab's business rules - auto-STAT
  // diagnoses, GAE/plantar-fasciitis provider routing, HMO auth, required-fields fax-back - only
  // make sense under this workflow). Rule details land in workflow-steps.ts (phase 6).
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Referral",
    documentLabel: "New Patient Referral",
    documentTypeId: 391737,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    classification: Classification.REFERRAL,
    group: "cvvc",
  },
} as const;
